import { useEffect, useState, useMemo } from "react";
import { Workflow, Plus } from "lucide-react";
import { GenericEntityWorkspacePage } from "./shared/GenericEntityWorkspacePage";
import { Modal } from "../primitives/Modal";
import { Input } from "../primitives/Input";
import { Button } from "../primitives/Button";
import { Tabs } from "../primitives/Tabs";
import {
  listProcessTemplates,
  createProcessTemplate,
  updateProcessTemplate,
  listOpsFamilyCategories,
  createOpsFamilyCategory,
  type ProcessTemplate,
  type ProcessTemplateStatus,
  type OpsFamilyCategory,
} from "../../api/processes";
import type { EntityWorkspaceBoardConfig, EntityListItem } from "./shared/entityWorkspaceBoardConfigs";
import { ProcessConfigurationTab } from "../components/process/ProcessConfigurationTab";
import { ProcessActiveTasksTab } from "../components/process/ProcessActiveTasksTab";
import { ProcessVersionsTab } from "../components/process/ProcessVersionsTab";
import { ProcessHealthTab } from "../components/process/ProcessHealthTab";

export function ProcessBuilderPage({ userName = "Utilisateur" }: { userName?: string }) {
  const [templates, setTemplates] = useState<ProcessTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<ProcessTemplate | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"configuration" | "tasks" | "versions" | "health">("configuration");
  const [form, setForm] = useState({
    name: "",
    ops_family: "",
  });
  const [opsFamilies, setOpsFamilies] = useState<OpsFamilyCategory[]>([]);
  const [addFamilyOpen, setAddFamilyOpen] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState("");
  const [addingFamily, setAddingFamily] = useState(false);

  // Load ops family categories (for create/edit modal)
  useEffect(() => {
    listOpsFamilyCategories()
      .then(setOpsFamilies)
      .catch(() => setOpsFamilies([]));
  }, [addFamilyOpen]);

  const loadTemplates = () => {
    return listProcessTemplates({ page_size: 200 })
      .then((data) => {
        setTemplates(data.items || []);
        return data.items ?? [];
      })
      .catch((error) => {
        console.error("Failed to load process templates:", error);
        alert(`Erreur lors du chargement des processus: ${error.message || error}`);
        return [];
      });
  };

  // Load templates on mount
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    loadTemplates().then((items) => {
      if (!mounted) return;
      if (items.length > 0 && !selectedId) {
        setSelectedId(items[0].id);
      }
      setIsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Convert templates to entity list items
  const entityListItems: EntityListItem[] = useMemo(() => {
    return templates.map((template) => ({
      id: template.id,
      title: template.name,
      subtitle: template.ops_family || undefined,
      info: template.status === "published" ? "Publié" : "Brouillon",
      icon: <Workflow size={16} strokeWidth={1.5} />,
      status: {
        label: template.status === "published" ? "Publié" : template.status === "archived" ? "Archivé" : "Brouillon",
        tone: template.status === "published" ? "success" : template.status === "archived" ? "neutral" : "warning",
      },
    }));
  }, [templates]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedId);
  }, [templates, selectedId]);

  const resetForm = () => {
    setForm({ name: "", ops_family: "" });
    setErrors({});
  };

  const handleSubmit = async () => {
    setErrors({});
    if (!form.name.trim()) {
      setErrors({ name: "Le nom est requis" });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editTemplate) {
        await updateProcessTemplate(editTemplate.id, form);
      } else {
        await createProcessTemplate(form);
      }
      // Reload templates
      const data = await listProcessTemplates({ page_size: 200 });
      setTemplates(data.items || []);
      // Select the newly created/updated template
      if (data.items && data.items.length > 0) {
        const newTemplate = data.items.find((t) => 
          editTemplate ? t.id === editTemplate.id : t.name === form.name
        );
        if (newTemplate) {
          setSelectedId(newTemplate.id);
        }
      }
      setOpen(false);
      resetForm();
      setEditTemplate(null);
    } catch (error: any) {
      console.error("Failed to save process template:", error);
      const errorMessage = error.message || error.toString() || "Erreur inconnue";
      setErrors({ submit: errorMessage });
      alert(`Erreur lors de l'enregistrement: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const config: EntityWorkspaceBoardConfig = useMemo(
    () => ({
      topbarTitle: "Processus",
      entities: templates.map((t) => ({
        id: t.id,
        header: {
          title: t.name,
          subtitle: t.ops_family || undefined,
          metaItems: [
            { label: "Statut", value: t.status === "published" ? "Publié" : t.status === "archived" ? "Archivé" : "Brouillon" },
          ],
          status: {
            label: t.status === "published" ? "Publié" : t.status === "archived" ? "Archivé" : "Brouillon",
            tone: t.status === "published" ? "success" : t.status === "archived" ? "neutral" : "warning",
          },
        },
      })),
      entityListItems,
      boardSectionId: "configuration",
      sections: [
        {
          id: "configuration",
          label: "Configuration",
          active: activeTab === "configuration",
        },
        {
          id: "tasks",
          label: "Tâches actives",
          active: activeTab === "tasks",
        },
      ],
      listTitle: "Processus",
      listIcon: <Workflow size={16} />,
      entityType: "processus",
      storageKey: "xlflow.process.sidebar",
      defaultCollapsed: false,
      defaultPinned: false,
      allowHoverOpen: true,
    }),
    [templates, entityListItems, activeTab]
  );

  return (
    <>
      <GenericEntityWorkspacePage
        config={config}
        userName={userName}
        items={entityListItems}
        selectedId={selectedId}
        onSelect={setSelectedId}
        addActionLabel="+ Nouveau processus"
        onAddAction={() => {
          resetForm();
          setEditTemplate(null);
          setOpen(true);
        }}
        customContent={
          selectedTemplate ? (
            <div className="flex flex-col h-full">
              <Tabs
                tabs={[
                  { id: "configuration", label: "Configuration" },
                  { id: "tasks", label: "Tâches actives" },
                  { id: "versions", label: "Versions" },
                  { id: "health", label: "Santé" },
                ]}
                activeId={activeTab}
                onChange={(id) => setActiveTab(id as "configuration" | "tasks" | "versions" | "health")}
                variant="underline"
              />
              <div className="flex-1 overflow-y-auto">
                {activeTab === "configuration" ? (
                  <ProcessConfigurationTab
                    templateId={selectedTemplate.id}
                    onTemplateDeleted={() => {
                      setSelectedId(null);
                      loadTemplates();
                    }}
                  />
                ) : activeTab === "tasks" ? (
                  <ProcessActiveTasksTab templateId={selectedTemplate.id} />
                ) : activeTab === "versions" ? (
                  <ProcessVersionsTab templateId={selectedTemplate.id} />
                ) : (
                  <ProcessHealthTab templateId={selectedTemplate.id} />
                )}
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              Sélectionnez un processus ou créez-en un nouveau
            </div>
          )
        }
      />

      {/* Create/Edit Modal */}
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={editTemplate ? "Modifier le processus" : "Nouveau processus"}
        showDefaultButtons={false}
        showCloseButton={true}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-text">Nom *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nom du processus"
              error={errors.name}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-text">Catégorie / Famille (optionnel)</label>
            <div className="flex gap-2 items-center flex-wrap">
              <select
                className="rounded border border-border bg-surface px-2 py-1.5 text-sm min-w-[180px]"
                value={form.ops_family}
                onChange={(e) => setForm({ ...form, ops_family: e.target.value })}
              >
                <option value="">— Aucune —</option>
                {opsFamilies.map((f) => (
                  <option key={f.id} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </select>
              <Button
                label="Ajouter une famille"
                intent="secondary"
                size="sm"
                onClick={() => setAddFamilyOpen(true)}
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Optionnel. Pour filtrer ou étiqueter les processus (UX, pas de logique métier).
            </p>
          </div>
          <Modal
            open={addFamilyOpen}
            onOpenChange={setAddFamilyOpen}
            title="Nouvelle catégorie de processus"
            showDefaultButtons={false}
            showCloseButton={true}
            size="sm"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Nom de la famille</label>
                <Input
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  placeholder="ex: Licence, Formation, Vol drone"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  label="Annuler"
                  intent="default"
                  size="sm"
                  onClick={() => {
                    setAddFamilyOpen(false);
                    setNewFamilyName("");
                  }}
                />
                <Button
                  label={addingFamily ? "Enregistrement…" : "Créer"}
                  intent="primary"
                  size="sm"
                  state={addingFamily ? "loading" : "default"}
                  onClick={async () => {
                    const name = newFamilyName.trim();
                    if (!name) return;
                    setAddingFamily(true);
                    try {
                      const created = await createOpsFamilyCategory({ name });
                      setOpsFamilies((prev) => [...prev, created]);
                      setForm((f) => ({ ...f, ops_family: created.name }));
                      setAddFamilyOpen(false);
                      setNewFamilyName("");
                    } catch (err) {
                      alert((err as Error).message || "Erreur lors de la création");
                    } finally {
                      setAddingFamily(false);
                    }
                  }}
                />
              </div>
            </div>
          </Modal>
          {errors.submit && (
            <div className="rounded-md border border-danger bg-danger/10 p-3">
              <div className="text-sm text-danger">{errors.submit}</div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              onClick={() => {
                setOpen(false);
                resetForm();
                setEditTemplate(null);
              }}
              state={isSubmitting ? "disabled" : "default"}
            />
            <Button
              label={isSubmitting ? "Enregistrement..." : editTemplate ? "Enregistrer" : "Créer"}
              intent="primary"
              size="sm"
              onClick={handleSubmit}
              state={isSubmitting ? "loading" : "default"}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
